import {
  FocusEvent,
  forwardRef,
  InputHTMLAttributes,
  KeyboardEvent,
  useCallback,
  useState,
} from 'react';

import { ErrorMessage } from './ErrorMessage';

interface IAutocompleteInputProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'inputMode' | 'defaultValue'
  > {
  readonly label: string;

  readonly onSuggestionClick: (value: unknown) => void;
  readonly suggestions: ReadonlyArray<{
    readonly value: unknown;
    readonly label: string;
  }>;

  readonly errors?: ReadonlyArray<string>;
}

export const AutocompleteInput = forwardRef<
  HTMLInputElement,
  IAutocompleteInputProps
>(function AutocompleteInput(props, ref) {
  const {
    errors = [],
    label,
    type,
    suggestions,
    onSuggestionClick,
    onKeyDown,
    required,
    readOnly,
    disabled,
    ...inputProps
  } = props;
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);

  const onKeyDownHandler = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key?.toLowerCase() === 'escape') {
        setShowSuggestions(false);
      }

      if (onKeyDown) {
        onKeyDown(event);
      }
    },
    [onKeyDown],
  );

  const onSuggestionClickHander = useCallback(
    (value: unknown) => {
      onSuggestionClick(value);
      setShowSuggestions(false);
    },
    [onSuggestionClick],
  );

  const onWrapperBlur = useCallback((event: FocusEvent) => {
    if (!event.relatedTarget) {
      // FIXME: delay hiding the suggestions to allow possible click
      // in one of the suggestions
      setTimeout(() => {
        setShowSuggestions(false);
      }, 150);
    }
  }, []);

  const onWrapperFocus = useCallback(() => {
    setShowSuggestions(true);
  }, []);

  const inputType = (type === 'number' ? 'text' : type) ?? 'text';
  const inputMode = type === 'number' ? 'numeric' : undefined;

  return (
    <div
      className="pmi_web_inputs__wrapper pmi_web_inputs__autocomplete"
      onBlur={onWrapperBlur}
      onFocus={onWrapperFocus}
      data-disabled={readOnly || disabled}
    >
      <label
        className={'pmi_web_inputs__inputlabel'}
        data-errors={errors.length > 0}
      >
        <input
          {...inputProps}
          type={inputType}
          inputMode={inputMode}
          ref={ref}
          onKeyDown={onKeyDownHandler}
          data-required={required}
          readOnly={readOnly}
          disabled={disabled}
        />
        <span>{label}</span>
      </label>
      {showSuggestions && suggestions.length > 0 && (
        <ul>
          {suggestions.map((s, index) => (
            <li key={`${index}_${s.label}`}>
              <button onClick={() => onSuggestionClickHander(s.value)}>
                {s.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      {errors.map(error => (
        <ErrorMessage key={error} message={error} />
      ))}
    </div>
  );
});
